const header = {
  general: 'GENERAL PROVISIONS AND PUBLIC OFFER',
  terms: 'TERMS AND DEFINITIONS',
  objectAgreement: 'SUBJECT OF THE AGREEMENT',
  legalNature: 'LEGAL NATURE OF THE CHARGING SERVICE',
  acceptance: 'ACCEPTANCE, ELECTRONIC FORM AND ACCESSION',
  payment: 'FINANCE, TARIFFS AND PAYMENT',
  refund: 'REFUND OF UNUSED OR DISPUTED FUNDS',
  rightsAndResponsibilitySides: 'RIGHTS AND OBLIGATIONS OF THE USER',
  executor: 'RIGHTS AND OBLIGATIONS OF THE AGGREGATOR AND THE CONTRACTOR',
  liability: 'RESPONSIBILITY OF THE PARTIES',
  privacy: 'PRIVACY, PERSONAL DATA, BRAND AND PUBLIC MENTIONS',
  other: 'TERM, GOVERNING LAW AND OTHER CONDITIONS',
};

const introduction = [
  'This Agreement is a public offer, accession agreement, standard terms and conditions, electronic contract, click-wrap / browse-wrap agreement or other similar legally binding instrument to the maximum extent permitted by applicable law.',
  'This Agreement governs interaction between the Aggregator, the User, the Contractor, the Charging Station Owner and the Location Owner. The Contractor is the person who, in the relevant charging session, organizes and/or provides the User with a Charging Service under its own brand or under a brand available on the Platform, unless otherwise established by the Station Profile. The same person may simultaneously perform multiple roles under this Agreement.',
  'The Aggregator is the operator of the 220-km.com Platform. It provides access to the website and the mobile application, session start and technical payment integration. The Aggregator is not the provider of the Charging Service unless it itself acts as the Contractor for a specific charging session.',
  'Registration on the Platform, logging into the account, clicking the “Pay”, “Start”, “Charge” button or similar, scanning QR/NFC/RFID, starting a charging session, making a payment or continued use of the Platform means that you have accepted this Agreement in full.',
];

export const companyInfo = {
  name: 'ТОВ «мережа 220»',
  fullName: 'ТОВАРИСТВО З ОБМЕЖЕНОЮ ВІДПОВІДАЛЬНІСТЮ «роумінгова мережа зарядних станцій 220 км»',
  shortName: 'мережа 220',
  edrpou: '45977567',
  address: '02160, Україна, місто Київ, проспект Соборності, будинок 17 офіс 2-92',
};

const body = [
  {
    id: 1,
    title: header.general,
    href: '#general',
    texts: [
      {
        id: '1.1',
        text: 'For Ukraine, this Agreement may be considered a public agreement within the meaning of Article 633 of the Civil Code of Ukraine, an accession agreement within the meaning of Article 634 of the Civil Code of Ukraine, and a public offer within the meaning of Article 641 of the Civil Code of Ukraine. It has legal force under the Law of Ukraine “On Electronic Commerce”.',
      },
      {
        id: '1.2',
        text: 'The terms of this Agreement are the same for all persons who join it, except where the tariff, minimum payment or other conditions are expressly specified in the Station Profile, the Platform interface, the website or at the specific Charging Station before the charging session starts.',
      },
      {
        id: '1.3',
        text: 'The Aggregator has the right to amend this Agreement by publishing a new version on the website, mobile application or other interface of the Platform. Unless another term is specified in the new version, the amendments shall enter into force from the moment of publication. In case of disagreement with the changes, the User is obliged to stop using the Platform. Further use of the Platform after the changes come into force shall be deemed acceptance of such changes.',
      },
    ],
  },
  {
    id: 2,
    title: header.terms,
    href: '#agreement',
    texts: [
      {
        id: '2.1',
        text: 'Platform — the 220-km.com website, the mobile application and related services through which the User finds Charging Stations, pays and starts charging.',
      },
      {
        id: '2.2',
        text: 'User / EV Driver — an individual or legal entity that uses a mobile application, website, software, QR/NFC/RFID, Charging Station or other interface to receive an electric vehicle battery charging service.',
      },
      {
        id: '2.3',
        text: 'Aggregator / Platform Operator — the operator of the 220-km.com Platform, which provides digital access, display of stations and tariffs, session start and technical integration with the bank / Payment Processor. The Aggregator does not receive the User’s payment for the Charging Service, except where it itself is the Contractor at that station, and except for the Aggregator Fee if that fee is charged separately.',
      },
      {
        id: '2.4',
        text: 'Contractor / Charging Service Provider / Service Operator — a person who, in the relevant charging session, organizes and/or provides the User with a service for charging the battery of an electric vehicle under its own brand or under a brand available on the Platform, unless otherwise established by the Station Profile.',
      },
      {
        id: '2.5',
        text: 'Charging Station Owner / Station Owner — a person who is the owner, lessee, user or other legal possessor of a Charging Station. Location Owner / Landlord — a person who is the owner, lessee, user or other legal owner of the place where a Charging Station is located. The same person may simultaneously perform multiple roles; in such a case, the rights, obligations and remuneration under the relevant roles shall be aggregated.',
      },
      {
        id: '2.6',
        text: 'Charging Service — a comprehensive service consisting of providing technical access to the charging infrastructure and carrying out the process of charging the battery of an electric vehicle. The volume of the service is recorded in kWh as a technical calculation indicator.',
      },
      {
        id: '2.7',
        text: 'Charging session — the process of providing the Charging Service from start until completion or stop.',
      },
      {
        id: '2.8',
        text: 'Charging Service Fee — a single integrated fee that the User pays for the charging service and related payments, displayed to the User before the start of the charging session. It may include the cost of access to the charging infrastructure, the service component, technological costs, payment infrastructure commissions and taxes if they were displayed to the User.',
      },
    ],
  },
  {
    id: 3,
    title: header.objectAgreement,
    href: '#objectAgreement',
    texts: [
      {
        id: '3.1',
        text: 'The subject of this Agreement is the organization of interaction between the Aggregator, User, Contractor, Charging Station Owner and Location Owner in order to ensure the provision of services for charging electric vehicle batteries using Charging Stations connected to the 220-km.com Platform, including roaming-partner stations (in particular EVBOOST, UGV, EVA) if they are available in the interface.',
      },
      {
        id: '3.2',
        text: 'The Aggregator provides access to the Platform, station and tariff information, technical session start and payment integration. The Contractor organizes and/or provides the User with a Charging Service within a specific charging session. The User’s payment is received by the Contractor; the Aggregator is not a party to that payment, except where it itself is the Contractor at that station.',
      },
      {
        id: '3.3',
        text: 'The cost of the Charging Service is stated on 220-km.com for each station or port. The price does not include parking or site access if the Location Owner charges such a fee separately.',
      },
    ],
  },
  {
    id: 4,
    title: header.legalNature,
    href: '#legalNature',
    texts: [
      {
        id: '4.1',
        text: 'The User receives a comprehensive service for charging the battery of an electric vehicle, and not electrical energy as a separate product.',
      },
      {
        id: '4.2',
        text: 'The use of kWh, charging time, power, charging session or other technical parameters to calculate the cost of the service does not constitute the sale, resale or supply of electricity to the User as a separate product.',
      },
      {
        id: '4.3',
        text: 'For Ukraine, the parties take into account that in accordance with Part Five of Article 58 of the Law of Ukraine “On the Electricity Market”, the activity of providing services for charging electric energy storage systems of electric vehicles using electric charging stations is the consumption of electric energy and is not the supply of electric energy within the meaning of the said Law.',
      },
    ],
  },
  {
    id: 5,
    title: header.acceptance,
    href: '#acceptance',
    texts: [
      {
        id: '5.1',
        text: 'Acceptance of this Agreement is any action that indicates acceptance of its terms, in particular registration on the Platform, logging into the account, clicking the “Pay”, “Start”, “Charge” button or similar, scanning QR/NFC/RFID, connecting the vehicle to the port, starting a charging session or making a payment.',
      },
      {
        id: '5.2',
        text: 'Consent to receive the Charging Service also arises if the electric vehicle actually received a charge from 0.001 kWh. If you do not agree with the Agreement, do not use the Platform or the Charging Stations.',
      },
      {
        id: '5.3',
        text: 'Confirmation of the conclusion and execution of the Agreement may be Platform data, log files, records of charging sessions, checks, receipts, invoices, payment notifications and other electronic or paper documents.',
      },
    ],
  },
  {
    id: 6,
    title: header.payment,
    href: '#payment',
    texts: [
      {
        id: '6.1',
        text: 'The tariffs for charging services shall be displayed to the User before the start of the charging session. The cost of the service may be determined depending on the number of kWh as a technical calculation indicator, connector type, Charging Station power, location, time of day, dynamic tariff, battery SoC or other parameters displayed in the Platform.',
      },
      {
        id: '6.2',
        text: 'Payment shall be made by way of prepayment: the User selects a kWh volume or an amount and pays through an available Payment Processor. There is no separate balance top-up — each charging session is paid separately.',
      },
      {
        id: '6.3',
        text: 'If the charging session is interrupted or less energy is used than paid for, the unused volume is returned as a 220-km.com virtual balance and may be used in later sessions on the 220-km.com network or on roaming-partner stations available in the Platform.',
      },
      {
        id: '6.4',
        text: 'The Aggregator shall provide the User with access to charging services only if payment succeeds or the virtual balance is sufficient. In the event of a bank refusal, payment rejection, cancellation of authorization, or insufficient funds, the Aggregator shall have the right, where technically possible, to initiate the termination of access to the charging session or not to start it.',
      },
      {
        id: '6.5',
        text: 'No Idle Fee is charged after the completion of charging.',
      },
      {
        id: '6.6',
        text: 'Payment shall be made in hryvnia unless the interface states otherwise. For Ukraine, conversion shall be made in the hryvnia equivalent at the exchange rate of the National Bank of Ukraine, the payment system, the acquiring bank or another exchange rate used by the relevant payment infrastructure. Bank or payment infrastructure commissions may be charged under the rules of the relevant institution.',
      },
    ],
  },
  {
    id: 7,
    title: header.refund,
    href: '#refund',
    texts: [
      {
        id: '7.1',
        text: 'Funds paid for a duly provided Charging Service shall not be refunded, except in cases expressly provided for by applicable law, the rules of the payment system, or this Agreement.',
      },
      {
        id: '7.2',
        text: 'If funds were prepaid or debited from the User, but the relevant Charging Service was not actually provided, was provided only partially, or the User disputes the quality or fact of provision of the service, first use the virtual balance via charging history. To receive a refund to a bank account after a 0 kWh session, tap Refund in the 220 app and submit the request. Or contact support with the port number.',
      },
      {
        id: '7.3',
        text: 'The Aggregator shall review the User’s request, verify the data of the Platform, payment infrastructure and charging session. Review may take up to 60 working days depending on the User’s bank. The Aggregator aims to process requests sooner. If the funds refunded to the User had previously been transferred or credited to the Contractor, the Aggregator shall have the right to obtain compensation of such amount from the Contractor by withholding it from future payments.',
      },
      {
        id: '7.4',
        text: 'In the request, the User shall provide a phone number and reason. The session is linked automatically via the payment invoice.',
      },
    ],
  },
  {
    id: 8,
    title: header.rightsAndResponsibilitySides,
    href: '#rightsAndResponsibilitySides',
    texts: [
      {
        id: '8.1',
        text: 'The User has the right to:',
        subTexts: [
          {
            id: '8.1.1',
            subText:
              'receive information about the terms of use, tariffs, available chargers, payment procedure, minimum reservation amount and other essential conditions through the Aggregator’s website, application and other digital services;',
          },
          {
            id: '8.1.2',
            subText:
              'receive the paid Charging Service within the technical limits of the Charging Station, the vehicle and the electricity grid;',
          },
          {
            id: '8.1.3',
            subText: 'stop using the Platform at any time if there is no outstanding debt.',
          },
        ],
      },
      {
        id: '8.2',
        text: 'The User must:',
        subTexts: [
          {
            id: '8.2.1',
            subText: 'comply with this Agreement, safety rules and the laws of Ukraine;',
          },
          {
            id: '8.2.2',
            subText:
              'before starting charging, check the condition of the station, cable, connector and car socket for visible damage, contamination or dangerous signs. If problems are detected, the User is obliged not to start charging and to notify the support service;',
          },
          {
            id: '8.2.3',
            subText: 'pay for the Charging Service only with lawful means of payment;',
          },
          {
            id: '8.2.4',
            subText:
              'after the charging session, disconnect the vehicle and vacate the parking space as soon as possible;',
          },
          {
            id: '8.2.5',
            subText:
              'use the parking space directly adjacent to the charger exclusively for its intended purpose — to accommodate an electric vehicle that is in a charging state;',
          },
          {
            id: '8.2.6',
            subText:
              'not use faulty, homemade or uncertified cables, adapters, converters or modified connection solutions, or vehicles with manufacturer-unapproved changes to the power system;',
          },
          {
            id: '8.2.7',
            subText:
              'treat the Charging Station, cables, connectors, adapters, parking space and other property with care, and not damage equipment, software, the Contractor’s business reputation or the location;',
          },
          {
            id: '8.2.8',
            subText:
              'not share Platform access with other people so they can receive the Charging Service in the User’s name.',
          },
        ],
      },
      {
        id: '8.3',
        text: 'The User is not entitled to use the emergency stop button / EMO without reason. The use of the EMO button is allowed only in the event of an emergency or potentially dangerous situation. Interrupting the current supply using the EMO button may lead to increased wear of the contactors and electronics of the vehicle, as well as the contactors, electronics and power modules of the Charging Station.',
      },
      {
        id: '8.4',
        text: 'Adapters must not be used to connect to a station with power above 80 kW, including CCS2 → NACS and CCS2 → CCS1 for US electric vehicles. In case of using uncertified adapters or other actions that caused or could have caused damage, the User may be subject to compensation for actual damages from the User’s virtual balance in favor of the Contractor.',
      },
      {
        id: '8.5',
        text: 'The User understands that removing the connector under power may damage the vehicle and station contactors, as well as cause the connector or adapter contacts to ignite or even cause an electric shock. End the charging session in the application, via vehicle functionality or by notifying the support service, and do not remove the connector until the current supply is completely stopped.',
      },
      {
        id: '8.6',
        text: 'In the event of an unsuccessful attempt to start a charging session, the User takes into account that the station may retain authorization for a technical period to reconnect and restart charging. If a restart is not required, the User is obliged to complete the transaction independently or wait for its automatic completion.',
      },
    ],
  },
  {
    id: 9,
    title: header.executor,
    href: '#executor',
    texts: [
      {
        id: '9.1',
        text: 'The Aggregator ensures the functioning of the Platform, mobile application, billing, payment integration, monitoring, technical support and other digital infrastructure within its technical capabilities.',
      },
      {
        id: '9.2',
        text: 'The Aggregator has the right to suspend or restrict access to the User, Contractor or station in cases of violation of the Agreement, malfunction, danger, fraud, non-payment, chargeback, unreliable data, or payment infrastructure requirements, and to publish a new version of this Agreement.',
      },
      {
        id: '9.3',
        text: 'The Contractor organizes and/or provides the Charging Service, is responsible for the condition of its equipment, installation, power supply and safety at the station within its area of responsibility, independently determines tariffs within the functionality of the Platform, and receives the User’s payment.',
      },
      {
        id: '9.4',
        text: 'Neither the Aggregator nor the Contractor sells electricity to the User as a separate product. The distribution of funds paid by the User is an internal mechanism for mutual settlements between the participants of the model and does not change the legal nature of the Charging Service provided to the User.',
      },
    ],
  },
  {
    id: 10,
    title: header.liability,
    href: '#liability',
    texts: [
      {
        id: '10.1',
        text: 'Each party is responsible for failure to perform or improper performance of its obligations within the limits of its role, actual area of responsibility and applicable law.',
      },
      {
        id: '10.2',
        text: 'The User is liable for damage to the Charging Station, cables, connectors, location and Platform caused by the User’s acts, adapters, cables or vehicle, and for the accuracy of payment data.',
      },
      {
        id: '10.3',
        text: 'The Aggregator is not responsible for equipment malfunctions, installation quality, power supply, network condition, location availability, actions of the Station Owner, Location Owner, User, energy supplier, or payment infrastructure, if such circumstances are not under the direct control of the Aggregator. The Aggregator is not liable for queues at the station, an occupied parking space, vehicle safekeeping, charging quality due to the vehicle battery or BMS, the User not having their own cable, or the consequences of using uncertified adapters or cables.',
      },
      {
        id: '10.4',
        text: 'The Contractor aims to deliver the stated power, but actual charging speed depends on the vehicle, concurrent use of ports, the electricity grid and the weather.',
      },
      {
        id: '10.5',
        text: 'Nothing in this Agreement shall limit the liability of a party in cases where such limitation is prohibited by mandatory applicable law.',
      },
    ],
  },
  {
    id: 11,
    title: header.privacy,
    href: '#privacy',
    texts: [
      {
        id: '11.1',
        text: 'Any information obtained in connection with the conclusion or performance of this Agreement, in particular personal data, payment data, technical data, log files and charging session data, is confidential, unless otherwise follows from its nature or is provided for by law.',
      },
      {
        id: '11.2',
        text: 'By accepting the Agreement, the User consents to processing of personal data for providing the Charging Service, payments, support, security, accounting and service notices. The processing of personal data is carried out in accordance with applicable data protection laws, including the Law of Ukraine “On Personal Data Protection”. Data may be stored in Ukraine and abroad, including in cloud services.',
      },
      {
        id: '11.3',
        text: 'Data may be shared with the bank, Payment Processor, Contractor, roaming partner, public authorities upon a lawful request, and with the Aggregator’s successors in case of reorganization or sale of the platform business.',
      },
      {
        id: '11.4',
        text: 'The User agrees to receive service, operational, technical and payment messages (SMS, messengers, email, calls, push) about charging sessions and support, and to recording of support contacts, taking into account applicable legislation on electronic communications and personal data protection.',
      },
    ],
  },
  {
    id: 12,
    title: header.other,
    href: '#other',
    texts: [
      {
        id: '12.1',
        text: 'For the User, the Agreement is valid from the moment of acceptance and for the period of use of the Platform or completion of all obligations under charging sessions.',
      },
      {
        id: '12.2',
        text: 'This Agreement is subject to the law of Ukraine. Disputes are resolved through negotiations, claim correspondence or through the support service. If the dispute is not resolved, it is transferred to a court under the laws of Ukraine.',
      },
      {
        id: '12.3',
        text: 'If any provision of this Agreement is found to be invalid or unenforceable, this shall not affect the validity of the remaining provisions.',
      },
      {
        id: '12.4',
        text: 'Use of the Platform does not give the User any rights to the 220-km.com brand, code, design or business. Use of the logo, trademark or other designation of the Aggregator or the Contractor in a modified form without the prior written permission of the relevant party is prohibited.',
      },
    ],
  },
];

const structureDocument = {
  header,
  introduction,
  body,
};

export default structureDocument;
